/**
 * Formats a date string for display (avoiding timezone issues)
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string (e.g., "Jun 15, 2025")
 */
export const formatDateForDisplay = (dateString: string): string => {
  if (!dateString) return '';
  // Parse as local date to avoid timezone conversion
  const [year, month, day] = dateString.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Checks if an event is upcoming (today or in the future)
 * @param eventDate - Date string in YYYY-MM-DD format
 * @returns True if the event is today or in the future
 */
export const isEventUpcoming = (eventDate: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day
  
  const [year, month, day] = eventDate.split('-');
  const event = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  
  return event >= today;
};

/**
 * Checks if an event is in the past
 * @param eventDate - Date string in YYYY-MM-DD format
 * @returns True if the event is in the past
 */
export const isEventInPast = (eventDate: string): boolean => {
  return !isEventUpcoming(eventDate);
};

/**
 * Checks if registration is still open for an event.
 * Registration closes at 11:30 PM local time the night before race day.
 * @param eventDate - Date string in YYYY-MM-DD format
 * @returns True if registration is still open
 */
export const isRegistrationOpen = (eventDate: string): boolean => {
  const [year, month, day] = eventDate.split('-');
  const cutoff = new Date(parseInt(year), parseInt(month) - 1, parseInt(day) - 1, 23, 30, 0);
  return new Date() < cutoff;
};

/**
 * Returns a human-readable string for the registration cutoff
 * (11:30 PM the night before race day), e.g. "Jul 25, 2026 · 11:30 PM"
 * @param eventDate - Date string in YYYY-MM-DD format
 */
export const formatRegistrationDeadline = (eventDate: string): string => {
  if (!eventDate) return '';
  const [year, month, day] = eventDate.split('-');
  const cutoff = new Date(parseInt(year), parseInt(month) - 1, parseInt(day) - 1, 23, 30, 0);
  const datePart = cutoff.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  const timePart = cutoff.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  return `${datePart} · ${timePart}`;
};